import { last } from 'lodash';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { getLongDatePeriod } from '@libs/utils';
import { SemboDate } from '@libs/sembo-dates';
import { IngredientType } from '@libs/trip-planner-interfaces';
import type {
  Money,
  ChronologicalItinerary,
  FerryIngredientResponse,
  FlightIngredientResponse,
  ItineraryWithPrice,
  FerryTicketCategory,
  ItineraryMainIngredientResponseWithStaticInfo,
} from '@libs/trip-planner-interfaces';
import {
  isAvailableHotelIngredientResponseWithStaticInfo,
  isAvailableFerryIngredientResponse,
  isAvailableFlightIngredientResponse,
  isOwnStayIngredientResponseWithStaticInfo,
} from '@libs/search-service-smart-search';
import type { AppButtonState } from '@libs/ui';

import type {
  TripSummarySegmentPrice,
  TripSummarySegment,
  TripSummaryPrice,
} from '../../models';
import { TripSummaryPriceType } from '../../models';

const { t } = i18n.global;
const ingredientTypeTranslation: Partial<Record<IngredientType, string>> = {
  [IngredientType.Flight]: t('planner.transport.flight'),
  [IngredientType.Ferry]: t('planner.transport.ferry'),
  [IngredientType.Activity]: t('planner.activity'),
  [IngredientType.Hotel]: t('planner.hotel'),
};

export function getTravelDateRange(
  chronologicalItinerary: ChronologicalItinerary[]
): string {
  const firstDayTrip = chronologicalItinerary[0];
  const lastDayTrip = last(chronologicalItinerary);

  if (firstDayTrip && lastDayTrip) {
    const endDate = new SemboDate(lastDayTrip.startDate)
      .add({ days: lastDayTrip.numberOfNights })
      .get();
    return getLongDatePeriod(firstDayTrip.startDate, endDate);
  }

  return '';
}

function getIngredientTypeTranslation(ingredientType: IngredientType): string {
  const translation = ingredientTypeTranslation[ingredientType];

  if (!translation) {
    throw new Error(
      `Could not find a translation for ${ingredientType} ingredient type!`
    );
  }

  return translation || ingredientType;
}

export function getTripSummaryPriceSegments(
  chronologicalItinerary: ChronologicalItinerary[],
  numberOfTravelers: number,
  addons: Record<string, FerryTicketCategory>,
  moneyFormanterFn: (amount: number, currency: string) => string
): TripSummarySegment[] {
  if (chronologicalItinerary.length === 0) {
    return [];
  }
  return chronologicalItinerary.reduce(
    (acc: TripSummarySegment[], itinerary: ChronologicalItinerary) => {
      itinerary.ingredients.forEach(
        (ingredient: ItineraryMainIngredientResponseWithStaticInfo) => {
          if (isAvailableHotelIngredientResponseWithStaticInfo(ingredient)) {
            // Accommodation segment
            acc.push({
              title: getIngredientTypeTranslation(ingredient.ingredientType),
              price: [
                {
                  description: ingredient.staticInfo.name,
                  value: moneyFormanterFn(
                    ingredient.suggested.totalPrice.amount,
                    ingredient.suggested.totalPrice.currency
                  ),
                  amount: ingredient.suggested.totalPrice.amount,
                  type: 'ingredient',
                },
              ],
            });
          }

          if (
            isAvailableHotelIngredientResponseWithStaticInfo(ingredient) ||
            isOwnStayIngredientResponseWithStaticInfo(ingredient)
          ) {
            // Activity segment
            ingredient.subIngredientResponseWithStaticInfo.forEach(
              (subingredient) => {
                if (subingredient.suggested?.totalPrice) {
                  acc.push({
                    title: getIngredientTypeTranslation(
                      subingredient.ingredientType
                    ),
                    price: [
                      {
                        description: subingredient.staticInfo.name,
                        value: moneyFormanterFn(
                          subingredient.suggested.totalPrice.amount,
                          subingredient.suggested.totalPrice.currency
                        ),
                        amount: subingredient.suggested.totalPrice.amount,
                        type: 'ingredient',
                      },
                    ],
                  });
                }
              }
            );
          }

          // Flight segment
          if (isAvailableFlightIngredientResponse(ingredient)) {
            const priceValue =
              ingredient.legIndex === 0
                ? moneyFormanterFn(
                    ingredient.totalPrice.amount,
                    ingredient.totalPrice.currency
                  )
                : t('planner.included').toLowerCase();

            const priceAmount =
              ingredient.legIndex === 0 ? ingredient.totalPrice.amount : 0;

            acc.push({
              title: getIngredientTypeTranslation(ingredient.ingredientType),
              price: [
                {
                  description: `${
                    ingredient.staticInfo.ports[ingredient.departureIata]
                      ?.position.name
                  } - ${
                    ingredient.staticInfo.ports[ingredient.arrivalIata]
                      ?.position.name
                  }`,
                  value: priceValue,
                  amount: priceAmount,
                  type: 'ingredient',
                },
              ],
            });
          }

          // Ferry segment
          if (isAvailableFerryIngredientResponse(ingredient)) {
            const price: TripSummarySegmentPrice[] = [
              {
                description: `${
                  ingredient.staticInfo.ports[ingredient.departureIata]
                    ?.position.name
                } - ${
                  ingredient.staticInfo.ports[ingredient.arrivalIata]?.position
                    .name
                }`,
                value: moneyFormanterFn(
                  ingredient.totalPrice.amount,
                  ingredient.totalPrice.currency
                ),
                amount: ingredient.totalPrice.amount,
                type: 'ingredient',
              },
            ];

            const addon = addons[ingredient.addToBasketToken];
            if (addon) {
              const addonPrice: Money = {
                ...addon.price,
                amount: addon.price.amount - ingredient.totalPrice.amount,
              };
              if (addonPrice.amount > 0) {
                price.push({
                  description: `${numberOfTravelers} ${addon.type} ${t(
                    'planner.tickets',
                    numberOfTravelers
                  )}`.toLowerCase(),
                  value: moneyFormanterFn(
                    addonPrice.amount,
                    addonPrice.currency
                  ),
                  amount: addonPrice.amount,
                  type: 'addon',
                });
              }
            }

            acc.push({
              title: getIngredientTypeTranslation(ingredient.ingredientType),
              price,
            });
          }
        }
      );

      return acc;
    },
    []
  );
}

export function isEveryIngredientAvailableFn(
  itinerary: ItineraryWithPrice | undefined
): boolean {
  if (!itinerary) {
    return false;
  }

  const isEveryFerryTransportAvailable = itinerary.ferryOnlyItinerary
    .map((ferry: FerryIngredientResponse) => ferry.isAvailable)
    .every((isAvailable: boolean) => isAvailable);

  if (!isEveryFerryTransportAvailable) {
    return false;
  }

  const isEveryFlightTransportAvailable = itinerary.flightOnlyItinerary
    .map((flight: FlightIngredientResponse) => flight.isAvailable)
    .every((isAvailable: boolean) => isAvailable);

  if (!isEveryFlightTransportAvailable) {
    return false;
  }

  const isEveryHotelAvailable = itinerary.hotelOnlyItinerary.every(
    (hotel) => hotel.isAvailable
  );

  if (!isEveryHotelAvailable) {
    return false;
  }

  const isEveryActivityAvailable = [
    ...itinerary.hotelOnlyItinerary,
    ...itinerary.ownStayOnlyItinerary,
  ]
    .flatMap((stay) => stay.subIngredientResponse)
    .every((activity) => activity.isAvailable);

  return isEveryActivityAvailable;
}

interface GetTripSummaryPriceParams {
  totalPrice: Money | null | undefined;
  totalPriceWithAlternative: Money | null | undefined;
  grossPrice: Money | null | undefined;
  grossPriceWithAlternative: Money | null | undefined;
}

export function getTripSummaryPrice(
  params: GetTripSummaryPriceParams
): TripSummaryPrice | undefined {
  if (params.totalPrice && !params.grossPrice) {
    return {
      totalPrice: params.totalPrice,
      grossPrice: undefined,
      priceType: TripSummaryPriceType.Total,
    };
  }

  if (params.totalPrice && params.grossPrice) {
    return {
      totalPrice: params.totalPrice,
      grossPrice: params.grossPrice,
      priceType: TripSummaryPriceType.TotalGross,
    };
  }

  if (params.totalPriceWithAlternative && !params.grossPriceWithAlternative) {
    return {
      totalPrice: params.totalPriceWithAlternative,
      grossPrice: undefined,
      priceType: TripSummaryPriceType.TotalAlternative,
    };
  }

  if (params.totalPriceWithAlternative && params.grossPriceWithAlternative) {
    return {
      totalPrice: params.totalPriceWithAlternative,
      grossPrice: params.grossPriceWithAlternative,
      priceType: TripSummaryPriceType.TotalGrossAlternative,
    };
  }

  return undefined;
}

export function getBookingButtonState(
  bookingInProgress: boolean,
  isEveryIngredientAvailable: boolean
): AppButtonState {
  if (bookingInProgress) {
    return 'loadingDots';
  }

  if (isEveryIngredientAvailable) {
    return 'enabled';
  }

  return 'disabled';
}

export function getTripTotalPrice(
  tripSummaryPrice: TripSummaryPrice | undefined
): number {
  return tripSummaryPrice?.totalPrice?.amount ?? 0;
}

export function getTripGrossPrice(
  tripSummaryPrice: TripSummaryPrice | undefined
): number {
  return tripSummaryPrice?.grossPrice?.amount ?? 0;
}
